import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, DatePicker, Modal } from 'antd';
import PropTypes from 'prop-types';
import fixedBottomDatePickerProps from '../../../../shared/utils/datePickerPlacement';
import { getTherapyPatientName } from '../../utils/therapyPresentation';

const durationOptions = ['1', '2', '3', '4', '5', '6'];

const getInitialForm = () => ({
  centralDate: dayjs().format('YYYY-MM-DD'),
  durationMonths: '',
});

const BulkRenewTherapiesModal = ({
  isOpen,
  therapies = [],
  loading = false,
  error = '',
  onClose,
  onRenew,
}) => {
  const [form, setForm] = useState(getInitialForm);

  useEffect(() => {
    if (!isOpen) return;

    setForm(getInitialForm());
  }, [isOpen]);

  const patientNames = useMemo(() => {
    const names = therapies
      .map((therapy) => getTherapyPatientName(therapy))
      .filter((name) => name && name !== '-');

    return [...new Set(names)];
  }, [therapies]);

  const patientNamesLabel = patientNames.length ? patientNames.join(', ') : '-';

  const handleRenew = useCallback(() => {
    const centralDate = form.centralDate?.trim();

    if (!centralDate || !form.durationMonths) {
      return;
    }

    onRenew?.({
      centralDate: `${centralDate}T00:00:00.000Z`,
      durationMonths: Number(form.durationMonths),
    });
  }, [form.centralDate, form.durationMonths, onRenew]);

  const handleClose = () => {
    if (loading) return;

    onClose?.();
  };

  return (
    <Modal
      className="create_user_modal"
      title="Obnovite terapiju"
      open={isOpen}
      onCancel={handleClose}
      footer={[
        <Button
          key="cancel"
          type="default"
          onClick={handleClose}
          disabled={loading}
        >
          Otkazi
        </Button>,
        <Button
          key="renew"
          type="primary"
          onClick={handleRenew}
          loading={loading}
          disabled={
            !form.centralDate || !form.durationMonths || !therapies.length
          }
        >
          Obnovi
        </Button>,
      ]}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <div style={{ marginBottom: 16 }}>
          <strong>Pacijenti:</strong> {patientNamesLabel}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="bulkRenewCentralDate"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Centralni datum
          </label>
          <DatePicker
            {...fixedBottomDatePickerProps}
            id="bulkRenewCentralDate"
            placeholder="dd/mm/yyyy"
            format="DD/MM/YYYY"
            style={{ width: '100%', height: 38 }}
            value={form.centralDate ? dayjs(form.centralDate) : null}
            onChange={(date) =>
              setForm((prev) => ({
                ...prev,
                centralDate: date ? date.format('YYYY-MM-DD') : '',
              }))
            }
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label
            htmlFor="bulkRenewDurationMonths"
            style={{ display: 'block', marginBottom: 6 }}
          >
            Trajanje
          </label>
          <select
            id="bulkRenewDurationMonths"
            name="durationMonths"
            value={form.durationMonths}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                durationMonths: event.target.value,
              }))
            }
            style={{
              width: '100%',
              height: 38,
              borderRadius: 8,
              border: '1px solid #d9d9d9',
              padding: '0 10px',
            }}
          >
            <option value="" disabled>
              Izaberite trajanje
            </option>
            {durationOptions.map((value) => (
              <option key={value} value={value}>
                {value} {value === '1' ? 'mesec' : 'meseca'}
              </option>
            ))}
          </select>
        </div>
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

BulkRenewTherapiesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  therapies: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRenew: PropTypes.func.isRequired,
};

export default BulkRenewTherapiesModal;
